import React, { useEffect, useMemo, useRef, useState, useCallback, Fragment } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import axios from "../../plugins/axios"
import { pixelByMilliVoltage, colorByType } from "../../plugins/common";
import { useRecoilState } from "recoil";
import { selectBedInfoState, alarmReviewSelectedTimeState } from "../../plugins/recoil";

import backgroundImage from '../../assets/img/chart_bg.png';
import Dropdown from './ReviewDropdown'

// ChartJS.register(CategoryScale, LinearScale, Filler);

const AlarmReviewChart = ({ isAlarmFull, selectedAlarmInfo, speed, waveStartTime, waveEndTime }) => {
    const { t } = useTranslation()
    const areaRefs = useRef([])
    const waveAreaContainerRef = useRef(null)
    const [selectBedInfo] = useRecoilState(selectBedInfoState)
    const [chartWidth, setChartWidth] = useState(0)
    const [chartHeight, setChartHeight] = useState(0)
    const [dragging, setDragging] = useState(false)
    const [startPosition, setStartPosition] = useState(null)
    const [currentPosition, setCurrentPosition] = useState(null)
    const [endPosition, setEndPosition] = useState(null)
    const [elapsedTime, setElapsedTime] = useState(0)
    const [waveCodeList, setWaveCodeList] = useState([])
    const [waveParameters, setWaveParameters] = useState([])
    const [activeWaveIndex, setActiveWaveIndex] = useState(null)
    const [selectedTimePosition, setSelectedTimePosition] = useState(null)
    const [selectedTime, setSelectedTime] = useRecoilState(alarmReviewSelectedTimeState)


    const [dropdownStates, setDropdownStates] = useState({
        wave1: false,
        wave2: false,
        wave3: false,
        wave4: false
    });

    const [waveDatas, setWaveDatas] = useState({
        wave1: [],
        wave2: [],
        wave3: [],
        wave4: []
    })

    const [waveYAxisRange, setWaveYAxisRange] = useState({
        wave1: { min: 0, max: 100 },
        wave2: { min: 0, max: 100 },
        wave3: { min: 0, max: 100 },
        wave4: { min: 0, max: 100 }
    });

    const [wavePoints, setWavePoints] = useState({
        wave1: [],
        wave2: [],
        wave3: [],
        wave4: []
    })

    const pixelsPerSecond = useMemo(() => {
        const basePixelsPerSecond = 90; // 25mm/s 기준 (90픽셀 = 25mm)
        const baseSpeed = 25; // 기준 속도 25mm/s
        return (basePixelsPerSecond * (speed.value || baseSpeed)) / baseSpeed;
    }, [speed]);

    const timeLabels = useMemo(() => {
        const labels = [];
        const start = moment(waveStartTime);
        const end = moment(waveEndTime);
        const current = start.clone().startOf('second');
        
        while (current <= end) {
            const secondsFromStart = current.diff(start, 'seconds');
            const xPosition = secondsFromStart * pixelsPerSecond;
            
            labels.push({
                time: current.format('HH:mm:ss'),
                xPosition: xPosition,
                secondsFromStart: secondsFromStart
            });
            
            current.add(1, 'second');
        }
        
        return labels;
    }, [waveStartTime, waveEndTime, pixelsPerSecond])

    const handleClickDropdown = (type, val) => {
        setDropdownStates(prev => ({
          ...prev,
          [type]: typeof val !== 'undefined' ? false : !prev[type],
        }));
    };

    const handleTimeClick = (timePosition) => {
        setSelectedTimePosition(timePosition);

        const secondsFromStart = timePosition / pixelsPerSecond;
        const actualTime = moment(waveStartTime).add(secondsFromStart, 'seconds');
        setSelectedTime(actualTime);
    }

    const handleMouseDown = (e, waveIndex) => {
        const currentAreaRef = areaRefs.current[waveIndex];
        if (!currentAreaRef) return;
        
        const bounds = currentAreaRef.getBoundingClientRect();
        const mouseX = e.clientX - bounds.left;
        const mouseY = e.clientY - bounds.top;
        setDragging(true);
        setActiveWaveIndex(waveIndex);
        setStartPosition({ x: mouseX, y: mouseY });
        setCurrentPosition({ x: mouseX, y: mouseY });
        setEndPosition(null); 
        setElapsedTime(0);
    };

    const handleMouseMove = (e) => {
        if (!dragging || activeWaveIndex === null) return;
        const currentAreaRef = areaRefs.current[activeWaveIndex];
        if (!currentAreaRef) return;

        const bounds = currentAreaRef.getBoundingClientRect();
        const mouseX = Math.max(0, Math.min(e.clientX - bounds.left, bounds.width));
        const mouseY = Math.max(0, Math.min(e.clientY - bounds.top, bounds.height));
        setCurrentPosition({ x: mouseX, y: mouseY });
        
        // 모눈종이 기준으로 시간 계산 (pixelsPerSecond = gridSizePixels / timePerGrid)
        const pixelDistance = Math.abs(mouseX - startPosition.x);
        const elapsed = (pixelDistance / pixelsPerSecond).toFixed(2);
        setElapsedTime(elapsed);
    };

    const handleMouseUp = () => {
        if (!dragging) return;
        setDragging(false);
        setEndPosition(currentPosition); 
    };

    const handleChangeSelect = (val, index) => {
        const updatedWaveParameters = [...waveParameters];
        updatedWaveParameters[index] = {
            ...updatedWaveParameters[index],
            measureParameterId: val.id,
            measureParameter: val.parameter,
            measureParameterName: val.name
        };
        setWaveParameters(updatedWaveParameters);
    }

    const handleChangeWaveData = () => {        
        const start = moment(waveStartTime).startOf("second");
        const end = moment(waveEndTime).startOf("second");
        const newWavePoints = { wave1: [], wave2: [] }
        const newWaveYAxisRange = { wave1: { minY: -150, maxY: 300 }, wave2: { minY: -150, maxY: 300 } }
        Object.keys(waveDatas).forEach((key, idx) => {
            const allSeconds = [];
            let current = start.clone();
            while (current <= end) {
                allSeconds.push({
                    timestamp: current.format(),
                    wave: [],
                });
                current.add(1, "second");
            }
            const mergedData = allSeconds.map((second) => {
                const found = waveDatas[key].find(
                    (item) => moment(item.timestamp).startOf("second").format() === second.timestamp
                );
                return found || second;
            });
    
            const newSegments = [];
            let currentSegment = [];
    
            mergedData.forEach((item) => {
                if (item.wave) {
                    if (item.wave.length === 0) {
                        if (currentSegment.length > 0) {
                            newSegments.push(currentSegment);
                            currentSegment = [];
                        }
                    } else {
                        const createdAtSec = moment(item.timestamp).diff(moment(waveStartTime), 'seconds');
                        item.wave.forEach((value, waveIdx) => {
                            currentSegment.push({
                                createdAtSec,
                                waveIdx,
                                waveCount: item.wave.length,
                                value,
                            });
                        });
                    }
                }
            });
    
            if (currentSegment.length > 0) {
                newSegments.push(currentSegment);
            }
    
            const combinedWave = mergedData.flatMap(item => item.wave);
            const defaultMinY = -150;
            const defaultMaxY = 300;
            
            let minY, maxY;
            
            if (combinedWave.length > 0) {
                const dataMinY = Math.min(...combinedWave);
                const dataMaxY = Math.max(...combinedWave);
                minY = Math.min(defaultMinY, dataMinY);
                maxY = Math.max(defaultMaxY, dataMaxY);
            } else {
                minY = defaultMinY;
                maxY = defaultMaxY;
            }
    
            const newPoints = newSegments.map((segment) => {
                return segment.map((pt) => {
                    const xBase = pt.createdAtSec * pixelsPerSecond;
                    const xOffset = (pt.waveIdx / (pt.waveCount - 1)) * pixelsPerSecond;
                    const x = xBase + xOffset;
                    const y = isNaN((chartHeight - ((pt.value - minY) / (maxY - minY)) * chartHeight)) 
                        ? chartHeight / 2 
                        : (chartHeight - ((pt.value - minY) / (maxY - minY)) * chartHeight);                
                    return `${x},${y}`;
                }).join(" ");
            });
            newWavePoints[`wave${idx + 1}`] = newPoints;
            newWaveYAxisRange[`wave${idx + 1}`] = { minY: minY, maxY: maxY };
        })
        setWavePoints(newWavePoints);
        setWaveYAxisRange(newWaveYAxisRange);
    }

    const getWaveCodeList = async () => {
        try {
            const waveCodeRes = await axios.get(`/measure/parameters/wave`);
            if (waveCodeRes.code != '200') throw waveCodeRes;            
            setWaveCodeList(waveCodeRes.data.parameters);
            const lead2 = waveCodeRes.data.parameters.find(item => item.parameter === 'LEADⅡ');
            const pleth = waveCodeRes.data.parameters.find(item => item.parameter === 'PLETH');
            const resp = waveCodeRes.data.parameters.find(item => item.parameter === 'RESP');
            const ibp1 = waveCodeRes.data.parameters.find(item => item.parameter === 'IBP1');
            if (isAlarmFull) {
                setWaveParameters([
                    { measureParameterId: lead2.id, measureParameter: lead2.parameter, measureParameterName: lead2.name },
                    { measureParameterId: pleth.id, measureParameter: pleth.parameter, measureParameterName: pleth.name },
                    { measureParameterId: resp.id, measureParameter: resp.parameter, measureParameterName: resp.name },
                    { measureParameterId: ibp1.id, measureParameter: ibp1.parameter, measureParameterName: ibp1.name }
                ])
            } else {
                setWaveParameters([
                    { measureParameterId: lead2.id, measureParameter: lead2.parameter, measureParameterName: lead2.name },
                    { measureParameterId: pleth.id, measureParameter: pleth.parameter, measureParameterName: pleth.name }
                ])
            }
        } catch (err) { console.warn(err) }
    }

    const getTrendWave = async () => {
        try{
            const newWaveDatas = {};
        
            const waveDataPromises = waveParameters.map(async (item, idx) => {
                const res = await axios.get(`/trend/waveforms?serialNo=${selectedAlarmInfo.serialNo}&type=${item.measureParameter}&startTime=${moment(waveStartTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment(waveEndTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`);
                if (res.code != '200') throw res;
                return {
                    key: `wave${idx + 1}`,
                    data: res.data.waveformData.waveforms.map((item) => ({ 
                        timestamp: item.timestamp, 
                        wave: item.samples 
                    }))
                };
            });

            const results = await Promise.all(waveDataPromises);
            results.forEach(result => {
                newWaveDatas[result.key] = result.data;
            });
            setWaveDatas(newWaveDatas);
        }
        catch (err) { 
            console.warn(err)
        }
    }

    useEffect(() => {
        setDragging(false);
        setStartPosition(null);
        setCurrentPosition(null);
        setEndPosition(null);
        setElapsedTime(0);
        setActiveWaveIndex(null);
        setSelectedTimePosition(null);
        setSelectedTime(null);
        const newTotalDurationSec = moment(waveEndTime).diff(moment(waveStartTime), 'seconds');
        const calculatedWidth = newTotalDurationSec * pixelsPerSecond;
    
        // 25mm 표시를 위한 최소 너비 (25mm = 90px + 텍스트 여백 약 50px)
        const minWidthFor25mm = 150;
        
        // 부모 컨테이너의 최대 너비 확인
        let maxWidth = Infinity;
        let parentWidth = 0;
        let height = 180
        if (waveAreaContainerRef.current) {
            const parentRect = waveAreaContainerRef.current.getBoundingClientRect();
            maxWidth = parentRect.width;
            parentWidth = parentRect.width;
            height = (parentRect.height - 18) / waveParameters.length;
        }
    
        // 최소 너비를 parentWidth로 설정
        const finalWidth = Math.max(calculatedWidth, minWidthFor25mm, parentWidth);
        setChartWidth(Math.min(finalWidth, maxWidth));
        setChartHeight(height)
    }, [ waveStartTime, waveEndTime, waveParameters ]);

    useEffect(() => {
        let height = 180
        if (waveAreaContainerRef.current) {
            const parentRect = waveAreaContainerRef.current.getBoundingClientRect();
            height = (parentRect.height - 18) / waveParameters.length;
        }
        setChartHeight(height)
    }, [selectedTime])

    useEffect(() => {
        getTrendWave()
    }, [ waveStartTime, waveEndTime, waveParameters ])

    useEffect(() => {
        handleChangeWaveData()
    }, [waveDatas, pixelsPerSecond, chartHeight])

    useEffect(() => {
        if (isAlarmFull) {
            if (waveParameters.length < 4) {
                const additionalParameters = waveCodeList.filter(item => 
                    !waveParameters.some(param => param.measureParameterId === item.id)
                ).slice(0, 2);
                
                setWaveParameters(prev => [...prev, ...additionalParameters.map(item => ({
                    measureParameterId: item.id,
                    measureParameter: item.parameter,
                    measureParameterName: item.name
                }))]);
            }
        } else {
            if (waveParameters.length > 2) {
                setWaveParameters(prev => prev.slice(0, 2));
            }
        }
    }, [isAlarmFull])

    useEffect(() => {
        getWaveCodeList()

        return () => {
            setSelectedTime(null);
        }
    }, []);

    return (
        <>
            {
                <div className="h-full" ref={waveAreaContainerRef}>
                    <div 
                        className="w-full h-[18px] overflow-hidden pr-[16px] relative"
                        style={{
                            width: chartWidth,
                            backgroundImage: `url(${backgroundImage})`,
                            // backgroundSize: 'contain',
                            // backgroundPosition: 'center',
                            backgroundRepeat: 'repeat', 
                        }}
                    >
                        {/* 시간 표시 */}
                        {timeLabels.map((label, index) => (
                            <div
                                key={`time-label-${index}`}
                                className={`absolute top-0 text-[10px] text-[#DEDEDE] font-light cursor-pointer px-1 py-0.5 rounded ${
                                    selectedTimePosition === label.xPosition 
                                        ? 'bg-[#000000] text-[#3DFF3D]' 
                                        : ''
                                }`}
                                style={{
                                    left: `${label.xPosition}px`,
                                    transform: 'translateX(-50%)'
                                }}
                                onClick={() => handleTimeClick(label.xPosition)}
                            >
                                {label.time}
                            </div>
                        ))}
                    </div>
                    {
                        waveParameters.map((item, idx) => {
                            return (
                                <Fragment key={`wave-area-${idx}`}>
                                    {
                                        waveDatas[`wave${idx + 1}`] && (
                                            <div
                                                key={`wave-area-${idx}`}
                                                ref={(el) => (areaRefs.current[idx] = el)}
                                                className="relative w-full overflow-hidden pr-[16px]"
                                                style={{
                                                    width: chartWidth,
                                                    backgroundImage: `url(${backgroundImage})`,
                                                    // backgroundSize: 'contain',
                                                    // backgroundPosition: 'center',
                                                    backgroundRepeat: 'repeat',
                                                }}
                                                onMouseDown={(e) => handleMouseDown(e, idx)}
                                                onMouseMove={handleMouseMove}
                                                onMouseUp={handleMouseUp}
                                                onMouseLeave={handleMouseUp}
                                            >
                                                <Dropdown
                                                    type={`wave-dropdown`}
                                                    isShow={dropdownStates[`wave${idx + 1}`]}
                                                    value={waveParameters[idx]?.measureParameterId}
                                                    selectedParameters={waveParameters}
                                                    valueArr={waveCodeList}
                                                    handleClick={(val) => { handleClickDropdown(`wave${idx + 1}`, val) }}
                                                    handleValueClick={(val) => { handleChangeSelect(val, idx) }}
                                                />
                                                {selectedTimePosition !== null && (
                                                    <div
                                                        className="absolute top-0 w-[1px] pointer-events-none"
                                                        style={{
                                                            left: `${selectedTimePosition}px`,
                                                            height: `${chartHeight}px`,
                                                            borderLeft: '1px dashed #CECECE'
                                                        }}
                                                    />
                                                )}
                                                {
                                                    waveDatas[`wave${idx + 1}`].length > 0 ? (
                                                        <>
                                                            <svg width={chartWidth} height={chartHeight}>
                                                                {wavePoints[`wave${idx + 1}`]?.map((pointString, index) => (
                                                                    <polyline
                                                                        key={index}
                                                                        points={pointString}
                                                                        fill="none"
                                                                        stroke={colorByType[waveParameters[idx].measureParameter]}
                                                                        strokeWidth={1}
                                                                    />
                                                                ))}
                                                                {
                                                                    ['LEADⅠ', 'LEADⅡ', 'LEADⅢ'].includes(waveParameters[idx].measureParameter) && (
                                                                        <>
                                                                            <line
                                                                                x1={2}
                                                                                y1={(chartHeight / 2) - (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                x2={12}
                                                                                y2={(chartHeight / 2) - (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            {/* 세로선 */}
                                                                            <line
                                                                                x1={7}
                                                                                y1={(chartHeight / 2) - (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                x2={7}
                                                                                y2={(chartHeight / 2) + (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            {/* 아래쪽 가로선 */}
                                                                            <line
                                                                                x1={2}
                                                                                y1={(chartHeight / 2) + (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                x2={12}
                                                                                y2={(chartHeight / 2) + (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2)}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            <text
                                                                                x={15}
                                                                                y={(chartHeight / 2) + (pixelByMilliVoltage(waveYAxisRange[`wave${idx + 1}`]?.minY, waveYAxisRange[`wave${idx + 1}`]?.maxY, waveParameters[idx].measureParameter, chartHeight) / 2) + 15}
                                                                                fill={`white`}
                                                                                fontSize="12"
                                                                                textAnchor="middle"
                                                                            >
                                                                                {'1mv'}
                                                                            </text>
                                                                        </>
                                                                    )
                                                                }
                                                                {
                                                                    idx === (waveParameters.length - 1) && (
                                                                        <g transform={`translate(2, ${chartHeight - 25})`}>
                                                                            <line
                                                                                x1={0}
                                                                                y1={0}
                                                                                x2={0}
                                                                                y2={8}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            {/* 25mm 길이의 메인 가로선 */}
                                                                            <line
                                                                                x1={0}
                                                                                y1={8}
                                                                                x2={90} // 25mm = 90px + 양쪽 여백 4px
                                                                                y2={8}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            {/* 오른쪽 세로선 */}
                                                                            <line
                                                                                x1={90}
                                                                                y1={0}
                                                                                x2={90}
                                                                                y2={8}
                                                                                stroke="white"
                                                                                strokeWidth={1}
                                                                            />
                                                                            {/* 25mm 텍스트 */}
                                                                            <text
                                                                                x={96}
                                                                                y={8}
                                                                                fill="white"
                                                                                fontSize="12"
                                                                                fontWeight="300"
                                                                            >
                                                                                25mm
                                                                            </text>
                                                                        </g>
                                                                    )
                                                                }
                                                            </svg>

                                                            {activeWaveIndex === idx && (dragging || endPosition) && startPosition && (
                                                                <div
                                                                    className="absolute px-2 py-1 font-bold text-white rounded bg-opacity-80 bg-black"
                                                                    style={{
                                                                        top: `${(startPosition.y + (endPosition?.y || currentPosition.y)) / 2}px`,
                                                                        left: `${(startPosition.x + (endPosition?.x || currentPosition.x)) / 2}px`,
                                                                        transform: "translate(-50%, -50%)",
                                                                    }}
                                                                >
                                                                    {elapsedTime}s
                                                                </div>
                                                            )}

                                                            {activeWaveIndex === idx && startPosition && (
                                                                <div
                                                                    className="absolute w-2.5 h-2.5 rounded-full border border-white"
                                                                    style={{
                                                                        top: `${startPosition.y}px`,
                                                                        left: `${startPosition.x}px`,
                                                                        transform: "translate(-50%, -50%)",
                                                                    }}
                                                                />
                                                            )}

                                                            {activeWaveIndex === idx && endPosition && (
                                                                <div
                                                                    className="absolute w-2.5 h-2.5 rounded-full border border-white"
                                                                    style={{
                                                                        top: `${endPosition.y}px`,
                                                                        left: `${endPosition.x}px`,
                                                                        transform: "translate(-50%, -50%)"
                                                                    }}
                                                                />
                                                            )}

                                                            {activeWaveIndex === idx && startPosition && (dragging || endPosition) && (
                                                                <div
                                                                    className="absolute h-0 origin-top-left border-t-[1px] border-white border-dashed"
                                                                    style={{
                                                                        top: startPosition.y,
                                                                        left: startPosition.x,
                                                                        width: Math.sqrt(
                                                                            ((endPosition?.x || currentPosition.x) - startPosition.x) ** 2 +
                                                                                ((endPosition?.y || currentPosition.y) - startPosition.y) ** 2
                                                                        ),
                                                                        transform: `rotate(${Math.atan2(
                                                                            (endPosition?.y || currentPosition.y) - startPosition.y,
                                                                            (endPosition?.x || currentPosition.x) - startPosition.x
                                                                        )}rad)`,
                                                                    }}
                                                                />
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="ml-[51px] pr-[16px] font-light flex items-center justify-center text-[#9F9F9F]" style={{ height: chartHeight}}>
                                                            {t('error.noDataAvailable')}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                </Fragment>
                            )
                        })
                    }
                </div>
            }
        </>
    );
};

export default AlarmReviewChart;
